import {useCallback, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {RootState} from '../../redux/store';
import {
  createUploadFileSessionId,
  uploadChunks,
  completeUploadSession,
  cancelUploadSession,
  deleteUploadedFile,
  initiateIntakeUpload,
  getUploadedChunksBySessionId,
} from '../../api/EvidenceSourceUpload';
import {
  DragAndDropUploadStatus,
  InitiateUploadPayload,
  UploadCompletedData,
  UploadMethod,
  UploadQueueItem,
  UploadSessionData,
} from '../../types/upload-new-eviidence/evidenceSourceTypes';
import {useToast} from '../../contexts/toastContext/ToastContext';
import {getAPIErrorDetail, showErrorToast} from '../../api/utils';
import {AxiosError} from 'axios';
import {t} from 'i18next';
import _ from 'lodash';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../permissions/usePermissionsByPage';
import {
  GA_ERROR_CODES,
  GA_ERROR_TYPES,
} from '../../constants/analyticsConstants';

export const useSequentialUploadQueue = () => {
  const [uploadQueueState, setUploadQueueState] = useState<UploadQueueItem[]>(
    []
  );
  const [isUploading, setIsUploading] = useState(false);
  const [isIntakeLoading, setIsIntakeLoading] = useState(false);

  const queueRef = useRef<UploadQueueItem[]>([]);
  const isProcessingRef = useRef(false);

  const clientId = useSelector(
    (state: RootState) => state.selectedClient.clientId
  );

  const projectId = useSelector(
    (state: RootState) =>
      state.uploadEvidenceSource.selectedProject?.projectId ?? ''
  );

  const {evidenceSourceSessionId, sourceDetails} = useSelector(
    (state: RootState) => state.uploadEvidenceSource
  );

  const {canManageEvidence} = usePermissionsByPage(
    PAGE_NAME.UPLOAD_EVIDENCE_SOURCE,
    projectId
  );

  const {showToast} = useToast();

  // sync queue
  const syncQueue = (queue: UploadQueueItem[]) => {
    queueRef.current = queue;
    setUploadQueueState(queue);
  };

  const updateItem = useCallback(
    (id: string, updates: Partial<UploadQueueItem>, shift = false) => {
      if (!queueRef.current.some((item) => item.id === id)) return;

      let updatedQueue = queueRef.current.map((item) =>
        item.id === id ? {...item, ...updates} : item
      );

      if (shift) {
        const updatedItem = updatedQueue.find((item) => item.id === id);
        updatedQueue = updatedQueue.filter((item) => item.id !== id);
        if (updatedItem) updatedQueue.push(updatedItem);
      }

      syncQueue(updatedQueue);
    },
    []
  );

  // generate Checksum
  const generateChecksum = useCallback(async (blob: Blob) => {
    const buffer = await blob.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);

    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }, []);

  const getNextPending = () =>
    queueRef.current.find(
      (item) => item.status === DragAndDropUploadStatus.InQueue
    );

  // step 1 - init upload
  const getUploadedChunks = useCallback(
    async (uploadSessionId: string, signal: AbortSignal) => {
      const response = await getUploadedChunksBySessionId(
        uploadSessionId,
        signal
      );

      return response.data as unknown as UploadSessionData;
    },
    []
  );

  const initiateUpload = useCallback(
    async (file: File, signal: AbortSignal): Promise<UploadSessionData> => {
      const payload = {
        fileName: file.name,
        fileSize: file.size,
        uploadMethod: UploadMethod.Http,
        mimeType: file.type || 'application/octet-stream',
      };

      const response = await createUploadFileSessionId(
        clientId,
        projectId,
        payload,
        evidenceSourceSessionId ?? '',
        signal
      );

      return response.data as unknown as UploadSessionData;
    },
    [clientId, projectId, evidenceSourceSessionId]
  );

  // step 2 - upload chunks
  const uploadChunkData = useCallback(
    async (
      uploadSessionId: string,
      chunkIndex: number,
      chunk: Blob,
      signal: AbortSignal
    ) => {
      const checksum = await generateChecksum(chunk);

      await uploadChunks(
        clientId,
        projectId,
        uploadSessionId,
        chunkIndex,
        checksum,
        chunk,
        evidenceSourceSessionId ?? '',
        signal
      );
    },
    [clientId, projectId, evidenceSourceSessionId, generateChecksum]
  );

  // step 3 - merge chunk or complere upload
  const completeUpload = useCallback(
    async (uploadSessionId: string, signal: AbortSignal) => {
      const response = await completeUploadSession(
        clientId,
        projectId,
        uploadSessionId,
        evidenceSourceSessionId ?? '',
        signal
      );
      return response.data as unknown as UploadCompletedData;
    },
    [clientId, projectId, evidenceSourceSessionId]
  );

  // cancel upload
  const cancelUpload = useCallback(
    async (fileId: string) => {
      const item = queueRef.current.find((f) => f.id === fileId);
      if (!item) return;

      if (item.uploadSessionId) {
        try {
          await cancelUploadSession(clientId, projectId, item.uploadSessionId);
          item.abortController?.abort();
          syncQueue(queueRef.current.filter((f) => f.id !== fileId));
        } catch (error) {
          const err = error as AxiosError;
          const isAbort =
            err.name === 'AbortError' || err.name === 'CanceledError';

          if (!isAbort) {
            showErrorToast(showToast, err, 'center');
          }
        }
      } else {
        syncQueue(queueRef.current.filter((f) => f.id !== fileId));
      }
    },
    [clientId, projectId, showToast]
  );

  const removeFile = useCallback(
    async (fileId: string) => {
      const item = queueRef.current.find((f) => f.id === fileId);
      if (!item) return;

      try {
        if (item.uploadSessionId) {
          await deleteUploadedFile(clientId, projectId, item.uploadSessionId);
          syncQueue(queueRef.current.filter((f) => f.id !== fileId));
        }
      } catch (error) {
        const err = error as AxiosError;
        showErrorToast(showToast, err, 'center');
      }
    },
    [clientId, projectId, showToast]
  );

  // process single file to upload
  const processSingleFile = useCallback(
    async (item: UploadQueueItem) => {
      const abortController = new AbortController();

      try {
        updateItem(item.id, {
          status: DragAndDropUploadStatus.InProcess,
          progress: 0,
          error: undefined,
          abortController,
        });

        // init upload
        let session: UploadSessionData | undefined;

        let initialChunkStartIndex = 1;
        let chunkSize;
        let totalChunks;
        if (item.uploadSessionId) {
          session = await getUploadedChunks(
            item.uploadSessionId,
            abortController.signal
          );
          initialChunkStartIndex = (session.uploadedChunks ?? 0) + 1;
          chunkSize = item.chunkSize ?? 0;
          totalChunks = item.totalChunks ?? 0;
        } else {
          session = await initiateUpload(item.file, abortController.signal);
          chunkSize = session.chunkSize;
          totalChunks = session.totalChunks;
          updateItem(item.id, {
            uploadSessionId: session.uploadSessionId,
            chunkSize,
            totalChunks,
          });
        }

        // chunks upload
        for (
          let chunkIndex = initialChunkStartIndex;
          chunkIndex <= totalChunks;
          chunkIndex++
        ) {
          if (abortController.signal.aborted) {
            throw new Error('Upload Cancelled');
          }

          const start = (chunkIndex - 1) * chunkSize;
          const end = Math.min(start + chunkSize, item.file.size);

          const chunk = item.file.slice(start, end);

          await uploadChunkData(
            session.uploadSessionId,
            chunkIndex,
            chunk,
            abortController.signal
          );

          updateItem(item.id, {
            progress: Math.round((chunkIndex / totalChunks) * 100),
          });
        }

        // merge chunk or complere upload
        const uploadCompleted = await completeUpload(
          session.uploadSessionId,
          abortController.signal
        );

        updateItem(item.id, {
          status: DragAndDropUploadStatus.Completed,
          progress: 100,
          abortController: undefined,
          storagePath: uploadCompleted.storagePath,
        });
      } catch (error: any) {
        const isAbort =
          error?.name === 'AbortError' ||
          error?.name === 'CanceledError' ||
          error?.message === 'Upload Cancelled';
        updateItem(item.id, {
          status: isAbort
            ? DragAndDropUploadStatus.Cancelled
            : DragAndDropUploadStatus.Failed,
          error: isAbort ? undefined : error?.message,
        });

        if (!isAbort) {
          const errorDetail = getAPIErrorDetail(error as AxiosError);
          const message = _.get(errorDetail, 'meta.message', '');
          showToast(
            t(message === '' ? 'uploadEvidence.failedError' : message),
            'error',
            undefined,
            'center',
            'bottom',
            {
              code: GA_ERROR_CODES.INTERNAL_SERVER_ERR_500,
              type: GA_ERROR_TYPES.FILE_UPLOAD_ERROR,
            }
          );
        }
      }
    },
    [
      completeUpload,
      getUploadedChunks,
      initiateUpload,
      showToast,
      updateItem,
      uploadChunkData,
    ]
  );

  // process upload queue
  const processQueue = useCallback(async () => {
    if (isProcessingRef.current) return;

    isProcessingRef.current = true;
    setIsUploading(true);

    try {
      let next = getNextPending();

      while (next) {
        await processSingleFile(next);
        next = getNextPending();
      }
    } finally {
      isProcessingRef.current = false;
      setIsUploading(false);
    }
  }, [processSingleFile]);

  // handle retry uploa
  const retryUpload = useCallback(
    async (fileId: string) => {
      const item = queueRef.current.find((f) => f.id === fileId);
      if (!item) return;

      updateItem(
        item.id,
        {
          status: DragAndDropUploadStatus.InQueue,
        },
        true
      );
      processQueue();
    },
    [processQueue, updateItem]
  );

  const addFiles = useCallback(
    (files: File[]) => {
      if (!files?.length) return;

      const newItems: UploadQueueItem[] = files.map((file) => ({
        id: crypto.randomUUID(),
        file,
        status: DragAndDropUploadStatus.InQueue,
        progress: 0,
      }));

      syncQueue([...queueRef.current, ...newItems]);
      processQueue();
    },
    [processQueue]
  );

  // abort all ongoing uploads
  const abortAllUploads = useCallback(() => {
    queueRef.current.forEach((item) => {
      item.abortController?.abort();
    });
  }, []);

  // Initiate Intake Upload Api call
  const handleInitiateUpload = useCallback(async () => {
    setIsIntakeLoading(true);

    const completedUploads = queueRef.current.filter(
      (f) => f.status === DragAndDropUploadStatus.Completed
    );
    const payload: InitiateUploadPayload = {
      name: sourceDetails?.evidenceName,
      clientReference: sourceDetails?.clientReference,
      hasManifest: !!sourceDetails?.uploadedManifest,
      hasPasswords: Boolean(sourceDetails?.password?.length),
      hasPasskey: (sourceDetails?.uploadedPasskey?.length ?? 0) > 0,
      hasSpecialHandling: !!sourceDetails?.handlingInstructions,
      passwords: sourceDetails?.password,
      custodianType: sourceDetails?.custodianType,
      custodianId: String(sourceDetails?.singleCustodian?.id ?? ''),
      custodianName: sourceDetails?.singleCustodian?.displayName ?? '',
      custodianFirstName: sourceDetails?.singleCustodian?.firstName ?? '',
      custodianLastName: sourceDetails?.singleCustodian?.lastName ?? '',
      processingTrigger: sourceDetails?.intakeCompletion.value,
      specialHandlingInstructions: sourceDetails?.handlingInstructions,
      additionalInstructions: sourceDetails?.isAdditionalInstructions,
      manifests: sourceDetails?.uploadedManifest
        ? {
            fileId: sourceDetails.uploadedManifest.fileId ?? '',
            fileSizeInBytes:
              sourceDetails.uploadedManifest.fileSizeInBytes ?? 0,
            fileName: sourceDetails.uploadedManifest.fileName ?? '',
            storagePath: sourceDetails.uploadedManifest.path ?? '',
          }
        : undefined,
      passkeys: sourceDetails?.uploadedPasskey
        ? sourceDetails.uploadedPasskey.map((uploadedPasskey) => {
            return {
              fileId: uploadedPasskey.fileId ?? '',
              fileSizeInBytes: uploadedPasskey.fileSizeInBytes ?? 0,
              fileName: uploadedPasskey.fileName ?? '',
              storagePath: uploadedPasskey.path ?? '',
            };
          })
        : undefined,
      uploadMethod: UploadMethod.Http,
      uploads: completedUploads.map((item) => ({
        fileId: item.uploadSessionId || '',
        fileSizeInBytes: item.file.size,
        fileName: item.file.name,
        storagePath: item.storagePath || '',
      })),
      auroraType:
        sourceDetails?.defaultProcessingSettings?.find(
          (item: any) => item.id === 'serviceType'
        )?.value ?? '',
    };

    try {
      const response = await initiateIntakeUpload(
        clientId,
        projectId,
        evidenceSourceSessionId ?? '',
        payload
      );
      showToast(
        t('fileCatalyst.intakeSuccess'),
        'success',
        undefined,
        'center'
      );
      return response.data;
    } catch (error) {
      showErrorToast(showToast, error as AxiosError, 'center');
      return null;
    } finally {
      setIsIntakeLoading(false);
    }
  }, [clientId, projectId, showToast, sourceDetails, evidenceSourceSessionId]);

  return {
    uploadQueueState,
    isUploading,
    isIntakeLoading,
    canManageEvidence,
    addFiles,
    cancelUpload,
    removeFile,
    abortAllUploads,
    handleInitiateUpload,
    retryUpload,
  };
};
